import axios from 'axios';
import { useState } from 'react';

type Download = { completed: number; assetData: string | null };

/**
 * This hook provides a *download* object which contains the download completion
 * percent and the asset blob data once a download has completed.
 *
 * It also exposes two methods:
 * - **startDownload**: Retrieves an asset from the provided URL and updates the
 *   *download* object accordingly.
 * - **clearDownload**: Resets any existing *download* object data. Note that this
 *   does *not* abort an active download.
 */
export function useAssetDirectDownload(): {
  clearDownload: () => void;
  startDownload: (url: string) => Promise<void>;
  download: Download;
} {
  const [download, setDownload] = useState<Download>({ completed: 0, assetData: null });

  /**
   * This function will begin downloading an asset from the provided URL.
   * It will periodically update download.completed and, when complete, will
   * populate download.assetData with the resulting blob data.
   * @param url Origin of asset resource
   */
  async function startDownload(url: string): Promise<void> {
    // Do not allow multiple concurrent downloads
    if (download.completed !== 0) return;

    // On slow connections, onDownloadProgress may not fire immediately.
    // We'll set progress to 0.01% to immediately indicate the download has begun.
    setDownload({ completed: 0.01, assetData: null });

    try {
      // Download the asset file; update completion progress periodically
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('id_token')}` },
        responseType: 'blob',
        onDownloadProgress: (event: ProgressEvent) => {
          // Without a content length there is no percent to show; keep the started state
          if (!event.total) return;

          // Determine progress percent and round to 2 decimal points
          const completed = Math.floor((event.loaded / event.total) * 10000) / 100;

          setDownload({ completed, assetData: null });
        },
      });

      // Set asset blob data when download is complete
      setDownload({ completed: 100, assetData: URL.createObjectURL(response.data) });
    } catch {
      clearDownload();
    }
  }

  /**
   * This function will reset the download.completed to 0 and will flush
   * any blob data that may be stored in download.assetData
   */
  function clearDownload(): void {
    setDownload({ completed: 0, assetData: null });
  }

  return { clearDownload, startDownload, download };
}
